/* CORNER mobile product page — 2026-09-29.
   Owns: the bottom sheets (open / close / swipe-down / backdrop / Escape) and the
   「カートに追加しました」 modal.

   The .cpdp-ready gate: this file adds that class to <html> only when <dialog> and
   showModal() actually exist. The stylesheet hides the pre-2026-09-29 entry points and
   shows the new ones only under that class, so a browser that cannot open a dialog
   keeps the old page intact instead of a page with dead buttons. LINE's and
   Instagram's in-app browsers on older iOS are the realistic case.

   It does NOT touch: .main-product-form, button[name="add"], input[name="id"],
   .product-mb__atwl, .product-form__actions, or sessionStorage['corner:browse:pending:v1'].
*/
(() => {
  'use strict';
  const SUPPORTED = typeof HTMLDialogElement === 'function'
    && typeof HTMLDialogElement.prototype.showModal === 'function';
  if (!SUPPORTED) return;
  document.documentElement.classList.add('cpdp-ready');

  /* Recommendation prices come back in subunits of the ACTIVE presentment currency, not always
     JPY — this storefront exposes a currency selector, and Shopify.currency.active reports what
     the visitor is actually being shown. Hardcoding ¥ would print a USD amount as yen.
     If the currency cannot be determined, the price line is omitted rather than guessed. */
  function money(subunits) {
    const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
    if (!code || typeof subunits !== 'number') return null;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ja-JP',
        { style: 'currency', currency: code }).format(subunits / 100);
    } catch (error) {
      return null;
    }
  }

  /* ---------- scroll lock ----------
     Driven by the dialogs' `open` attribute through a MutationObserver, NOT by the
     'close' event. Measured 2026-09-29 in a Chrome 152 mobile engine: showModal() and
     close() work, dialog.open flips correctly, but no 'close' (or 'cancel') event is
     delivered at all. A lock released only in a 'close' handler therefore stays on and
     the page behind the sheet can never be scrolled again. The `open` attribute cannot
     be missed, and it also covers Escape and any close we did not initiate.
     (The existing corner-product-support.js releases its lock in a 'close' handler, so
     the old dialog has the same exposure — noted in the README, not changed here.) */
  let locked = false, savedOverflow = '';
  // A top-layer dialog is still suppressed by a display:none ANCESTOR, and offsetParent is null
  // for fixed/top-layer elements even when they are visible — so neither test works alone. Walk up.
  function renderable(el) {
    if (!el || !el.isConnected) return false;
    for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
      if (getComputedStyle(node).display === 'none') return false;
    }
    return true;
  }
  function syncLock() {
    const anyOpen = [...document.querySelectorAll('dialog.cpdp-sheet[open]')].some(renderable);
    if (anyOpen && !locked) {
      savedOverflow = document.documentElement.style.overflow;
      // never restore to 'hidden' later: that would leave the page permanently unscrollable
      if (savedOverflow === 'hidden') savedOverflow = '';
      document.documentElement.style.overflow = 'hidden';
      locked = true;
    } else if (!anyOpen && locked) {
      document.documentElement.style.overflow = savedOverflow;
      locked = false;
    }
    if (!anyOpen) {
      document.querySelectorAll('dialog.cpdp-sheet .cpdp-sheet__panel')
        .forEach(panel => { panel.style.transform = ''; });
      document.querySelectorAll('dialog.cpdp-sheet.is-dragging')
        .forEach(sheet => sheet.classList.remove('is-dragging'));
    }
  }

  /* ---------- open / close ---------- */
  function open(dialog) {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    syncLock();
    dialog.dispatchEvent(new CustomEvent('cpdp:open'));
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('[data-cpdp-open]');
    if (trigger) {
      const dialog = document.getElementById(trigger.getAttribute('data-cpdp-open'));
      if (dialog) { event.preventDefault(); open(dialog); }
      return;
    }
    const closer = event.target.closest?.('[data-cpdp-close]');
    if (closer) {
      event.preventDefault();
      closer.closest('dialog')?.close();
      return;
    }
    // Backdrop: the click lands on the <dialog> itself, never on the panel inside it.
    const dialog = event.target;
    if (dialog instanceof HTMLDialogElement && dialog.classList.contains('cpdp-sheet') && dialog.open) {
      const box = dialog.getBoundingClientRect();
      if (event.clientY < box.top || event.clientY > box.bottom
        || event.clientX < box.left || event.clientX > box.right) dialog.close();
    }
  });


  /* A sheet that is open when the viewport crosses to >=768px would be hidden by its .cpdp
     ancestor going display:none — the dialog stays open and <html> stays overflow:hidden, so the
     page becomes unscrollable with nothing on screen to close. Rotating a phone to landscape is
     enough to hit it. Close everything on the way out of mobile. */
  // Both directions are unsafe, not just one:
  //   mobile -> desktop hides .cpdp, stranding an open sheet;
  //   desktop -> mobile hides .corner-legacy-mobile, stranding the legacy corner-assurance dialog
  //   (which this file did not create, but which is now inside the wrapper we added).
  // Close whatever can no longer be rendered, then re-derive the lock.
  const wide = window.matchMedia('(min-width: 768px)');
  function closeUnrenderable() {
    document.querySelectorAll('dialog[open]').forEach(d => { if (!renderable(d)) d.close(); });
    // corner-product-support.js releases its own lock in a 'close' handler, and that event does
    // not fire in every engine. If nothing at all is open, a leftover inline lock is stale.
    if (!document.querySelector('dialog[open]') && document.documentElement.style.overflow === 'hidden' && !locked) {
      document.documentElement.style.overflow = '';
    }
    syncLock();
  }
  const onBreakpoint = () => requestAnimationFrame(closeUnrenderable);
  if (typeof wide.addEventListener === 'function') wide.addEventListener('change', onBreakpoint);
  else if (typeof wide.addListener === 'function') wide.addListener(onBreakpoint);   // older WebKit
  // Theme-editor section re-render removes the dialogs without their `open` attribute changing.
  document.addEventListener('shopify:section:unload', closeUnrenderable);
  document.addEventListener('shopify:section:load', () => { bindSheets(); syncLock(); });

  /* If the product section is re-rendered (theme editor) the dialogs are removed without their
     `open` attribute ever changing, so the observers above never fire and the lock would stick.
     Watch the container's parent for that removal. */
  const container = document.querySelector('[data-cpdp]');
  if (container && container.parentNode) {
    new MutationObserver(() => {
      if (!document.querySelector('dialog.cpdp-sheet[open]')) syncLock();
    }).observe(container.parentNode, { childList: true });
  }

  /* Restoring from the back/forward cache keeps the old DOM state; re-derive the lock. */
  window.addEventListener('pageshow', syncLock);

  /* ---------- swipe down to close ----------
     Starts on the grab bar always, and on the body only when it is already scrolled to
     the top, so dragging the sheet never steals a scroll the customer meant for the text. */
  function bindSwipe(dialog) {
    const panel = dialog.querySelector('[data-cpdp-panel]');
    const body = dialog.querySelector('.cpdp-sheet__body');
    const grab = dialog.querySelector('[data-cpdp-grab]');
    if (!panel) return;
    let startY = 0, delta = 0, dragging = false, startedAt = 0;

    const begin = event => {
      if (event.touches.length !== 1) return;
      const fromGrab = grab && grab.contains(event.target);
      if (!fromGrab && body && body.contains(event.target) && body.scrollTop > 0) return;
      startY = event.touches[0].clientY;
      delta = 0; dragging = true; startedAt = Date.now();
      dialog.classList.add('is-dragging');
    };
    const move = event => {
      if (!dragging) return;
      delta = event.touches[0].clientY - startY;
      if (delta <= 0) { panel.style.transform = ''; return; }
      if (body && body.contains(event.target) && event.cancelable) event.preventDefault();
      panel.style.transform = `translateY(${delta}px)`;
    };
    const end = () => {
      if (!dragging) return;
      dragging = false;
      dialog.classList.remove('is-dragging');
      const fast = delta > 40 && (Date.now() - startedAt) < 300;
      if (delta > 90 || fast) { dialog.close(); return; }
      panel.style.transform = '';
    };

    panel.addEventListener('touchstart', begin, { passive: true });
    panel.addEventListener('touchmove', move, { passive: false });
    panel.addEventListener('touchend', end);
    panel.addEventListener('touchcancel', end);
  }

  // Bound per element and re-runnable: the theme editor replaces the whole section without
  // re-running page JS, so the replacement dialogs would otherwise have no swipe handling and
  // no `open` observer, and cartDialog would still point at the removed element.
  const bound = new WeakSet();
  function bindSheets() {
    document.querySelectorAll('dialog.cpdp-sheet').forEach(sheet => {
      if (bound.has(sheet)) return;
      bound.add(sheet);
      bindSwipe(sheet);
      new MutationObserver(syncLock).observe(sheet, { attributes: true, attributeFilter: ['open'] });
    });
    cartDialog = document.querySelector('[data-cpdp-cart]');
  }

  /* ---------- 「カートに追加しました」 modal ---------- */
  let cartDialog = document.querySelector('[data-cpdp-cart]');
  bindSheets();   // initial bind (also re-run on shopify:section:load)

  async function fillRecommendations() {
    const section = cartDialog.querySelector('[data-cpdp-recs]');
    const list = cartDialog.querySelector('[data-cpdp-recs-list]');
    if (!section || !list || list.childElementCount) return;
    const productId = cartDialog.getAttribute('data-product-id');
    if (!productId) return;
    let products = [];
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=6&intent=related`);
      if (!response.ok) return;                       // leave the section hidden
      products = (await response.json()).products || [];
    } catch (error) { return; }
    const usable = products.filter(p => p && p.url && p.title);
    if (!usable.length) return;                       // no empty heading
    const frag = document.createDocumentFragment();
    for (const p of usable) {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.className = 'cpdp-cart__item';
      a.href = p.url;
      const thumb = document.createElement('div');
      thumb.className = 'cpdp-cart__thumb';
      const src = p.featured_image || (p.images && p.images[0]);
      if (src) {
        const img = document.createElement('img');
        img.src = String(src).replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2');
        img.alt = ''; img.loading = 'lazy'; img.width = 64; img.height = 64;
        thumb.appendChild(img);
      }
      const text = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'cpdp-cart__name';
      name.textContent = p.title;
      text.appendChild(name);
      const formatted = money(p.price);
      if (formatted) {
        const price = document.createElement('div');
        price.className = 'cpdp-cart__price';
        price.textContent = formatted;
        text.appendChild(price);
      }
      a.append(thumb, text);
      li.appendChild(a);
      frag.appendChild(li);
    }
    list.appendChild(frag);
    section.hidden = false;
  }

  // The header bubble is the theme's; refresh it from /cart.js rather than reaching into
  // <m-cart-drawer> internals, so a theme update cannot silently break the count.
  async function refreshCartCount() {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cart = await fetch(`${root}cart.js`).then(r => r.json());
      document.querySelectorAll('.m-cart-count-bubble, .sf-cart-count, [data-cart-count]')
        .forEach(el => {
          el.textContent = cart.item_count;
          // theme.liquid's own updater hides it with a CLASS (classList.toggle('hidden', n<1)),
          // not the attribute — clearing only the attribute left the first add invisible.
          // Both are handled so either convention works.
          el.classList.toggle('hidden', cart.item_count < 1);
          if (cart.item_count > 0) el.removeAttribute('hidden');
          else el.setAttribute('hidden', '');
        });
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
    } catch (error) { /* the count is cosmetic; a failure here must not block the modal */ }
  }

  window.CornerPdp = {
    supported: true,
    async cartAdded() {
      refreshCartCount();
      // The request may finish after the viewport crossed to >=768px (rotate to landscape while
      // it is in flight). Opening the modal then would put an invisible dialog on screen and
      // lock the page, so the count refresh is all that happens.
      if (!cartDialog || window.matchMedia('(min-width: 768px)').matches) return false;
      open(cartDialog);
      fillRecommendations();
      return true;
    }
  };
})();
